import { CardBack } from '@/src/app/main/(tabs)/card/card-management/virtual-card/CardBack'
import { Routes } from '@/src/common/enums/routes'
import { useMemo, useState } from 'react'
import { StyleSheet, ScrollView, View, Text } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { router } from 'expo-router'
import { createShimmerPlaceholder } from 'react-native-shimmer-placeholder'
import { Button } from '@/src/common/components/Button'
import { HideIcon } from '@/src/assets/icons/Hide'
import { FreezeIcon } from '@/src/assets/icons/Freeze'
import { EyeIcon } from '@/src/assets/icons/EyeIcon'
import { CardFront } from '@/src/app/main/(tabs)/card/card-management/virtual-card/CardFront'
import { FrozenCard } from '@/src/app/main/(tabs)/card/card-management/virtual-card/FrozenCard'
import {
  useCombinedCardStatusStore,
  FrozenCardStatus,
  CARD_STATUS_LOADING,
} from '@/src/experiences/virtual-card/store/useCombinedCardStatusStore'
import { AccountLinkRequired } from '@/src/app/main/(tabs)/card/card-management/virtual-card/AccountLinkRequired'
import { UnfreezeIcon } from '@/src/app/main/(tabs)/card/card-management/virtual-card/UnfreezeIcon'
import { cardImageStyle } from '@/src/app/main/(tabs)/card/card-management/virtual-card/cardImageStyle'
import { AccountInReview } from './AccountInReview'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { CardHasShipped } from './CardHasShipped'
import { withVirtualCard } from '@/src/experiences/virtual-card/withVirtualCard'
import { mesaColorNames, typography } from '@/src/common/theme'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import * as Mortgage from '@/src/experiences/mortgage'

const ShimmerPlaceholder = createShimmerPlaceholder(LinearGradient)

export default withVirtualCard(function VirtualCard({
  cardLoaded,
  paymentMethodsLoaded,
  cardAccountActive,
  hasPaymentProfile,
  shipmentStatus,
  cardBackContent,
  freezeParams,
}) {
  const isVirtualCardWithoutPhysicalActivationEnabled = useFeatureGate(
    FeatureGates.VirtualCardWithoutPhysicalActivationEnabled,
  ).featureGate?.value

  const [showSensitiveInformation, setShowSensitiveInformation] = useState(false)
  const { status } = useCombinedCardStatusStore()
  const isCardFrozen = FrozenCardStatus.safeParse(status).success
  const [minimumWait, setMinimumWait] = useState<Promise<void> | 'done'>(() =>
    new Promise((resolve) => setTimeout(resolve, 1234)).then(() => {
      setMinimumWait('done')
    }),
  )

  const loaded =
    minimumWait === 'done' &&
    cardLoaded &&
    paymentMethodsLoaded &&
    status !== CARD_STATUS_LOADING &&
    !(typeof status === 'object' && 'error' in status)

  const hasCardToActivate =
    shipmentStatus === 'PENDING_ACTIVATION' || shipmentStatus === 'PENDING_FULFILLMENT'

  const cardViewDisabled =
    loaded &&
    (!hasPaymentProfile ||
      !cardAccountActive ||
      (!isVirtualCardWithoutPhysicalActivationEnabled && hasCardToActivate))

  const accountStatus = !loaded ? null : !cardAccountActive ? (
    // lock the card management to show the financial account is being reviewd
    // if we do not have an active financial account
    <AccountInReview />
  ) : !hasPaymentProfile ? (
    <AccountLinkRequired />
  ) : !isVirtualCardWithoutPhysicalActivationEnabled && hasCardToActivate ? (
    <CardHasShipped />
  ) : null

  const { data: userData } = useLoggedInQuery(GET_USER_INFO, {
    fetchPolicy: 'cache-first',
  })
  const sourcePartnerName = userData?.getUserInfo.sourcePartnerName ?? 'default'
  const parsedResult = Mortgage.Statsig.Store.MortgageModuleVariantName.safeParse(sourcePartnerName)
  const parsedSourcePartnerName = parsedResult.success ? parsedResult.data : 'default'

  const lakeViewDisclaimer =
    'The Mesa Homeowners Visa® Signature Preferred Credit Card for Lakeview is powered by Highnote and issued by Celtic Bank, a Utah-chartered industrial bank, Member FDIC, pursuant to a license from Visa U.S.A.. Visa is a registered trademark of Visa Inc.'
  const carringtonDisclaimer =
    'The Mesa Homeowners Card for Carrington is powered by Highnote and issued by Celtic Bank. Mesa Homeowners Visa® Signature Preferred Credit Card for Carrington Cardholder Agreement here. '
  //@TODO: need to update this disclaimer for TAB/LoanPro
  const defaultDisclaimer =
    'The Mesa Homeowners Card is issued by Celtic Bank and Powered by Highnote.'

  const disclaimer = useMemo(() => {
    switch (parsedSourcePartnerName) {
      case 'carrington':
        return carringtonDisclaimer
      case 'lakeview':
        return lakeViewDisclaimer
      case 'prmg':
      case 'encompass_lending_group':
      case 'alcova':
      case 'default':
        return defaultDisclaimer
      default:
        parsedSourcePartnerName satisfies never
        return defaultDisclaimer
    }
  }, [parsedSourcePartnerName])

  return (
    <ScrollView
      contentContainerStyle={{
        paddingBottom: 50,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <View
        style={[
          cardImageStyle,
          { marginHorizontal: 'auto', position: 'relative', overflow: 'hidden' },
        ]}
      >
        <ShimmerPlaceholder
          style={[cardImageStyle, { position: 'absolute', padding: undefined }]}
        />
        <CardFront
          frozen={isCardFrozen}
          style={{ position: 'absolute', opacity: loaded ? 1 : 0 }}
          imageBackgroundStyle={cardImageStyle}
        />
        <FrozenCard
          style={{
            position: 'absolute',
            opacity: loaded && isCardFrozen && !accountStatus ? 1 : 0,
          }}
          imageBackgroundStyle={cardImageStyle}
        />
        <CardBack
          frozen={!!isCardFrozen}
          style={{
            position: 'absolute',
            opacity: !isCardFrozen && showSensitiveInformation && loaded ? 1 : 0,
          }}
          imageBackgroundStyle={{ ...cardImageStyle, width: cardImageStyle.width + 2, left: -1 }}
        >
          {cardBackContent}
        </CardBack>
        {accountStatus}
      </View>
      {loaded ? (
        <>
          <View style={styles.buttonsContainer}>
            <View
              style={[
                styles.buttonOuterContainer,
                { opacity: isCardFrozen && !accountStatus ? 0.4 : 1 },
              ]}
            >
              <Button
                style={[
                  styles.button,
                  cardViewDisabled && {
                    backgroundColor: mesaColorNames['semantic/fg/disabled #898D8F'],
                  },
                ]}
                size="lg"
                onPress={() => setShowSensitiveInformation((x) => !x)}
                disabled={cardViewDisabled || isCardFrozen}
              >
                {showSensitiveInformation && hasPaymentProfile && !isCardFrozen ? (
                  <HideIcon />
                ) : (
                  <EyeIcon />
                )}
              </Button>
              <Text style={styles.buttonText}>View</Text>
            </View>
            <View style={styles.buttonOuterContainer}>
              <Button
                style={[
                  styles.button,
                  cardViewDisabled && {
                    backgroundColor: mesaColorNames['semantic/fg/disabled #898D8F'],
                  },
                ]}
                size="lg"
                disabled={cardViewDisabled}
                onPress={() =>
                  freezeParams &&
                  router.push({
                    pathname: '/main/(tabs)/card/card-management/virtual-card/freeze',
                    params: freezeParams,
                  })
                }
              >
                {isCardFrozen && !accountStatus ? (
                  <UnfreezeIcon
                    slashBackgroundColor={
                      cardViewDisabled
                        ? mesaColorNames['semantic/fg/disabled #898D8F']
                        : mesaColorNames['Mesa Primary Scale/500 #00C7B6']
                    }
                  />
                ) : (
                  <FreezeIcon />
                )}
              </Button>
              <Text style={styles.buttonText}>
                {isCardFrozen && !accountStatus ? 'Unfreeze' : 'Freeze'}
              </Text>
            </View>
          </View>
          {shipmentStatus === 'PENDING_ACTIVATION' && !cardViewDisabled ? (
            <View style={styles.activateCardContainer}>
              <Text style={styles.cardTitle}>Activate physical card</Text>
              <Text style={styles.cardBody}>
                Tap the button below to activate your physical card after you have received it in
                the mail.
              </Text>
              <Button onPress={() => router.push(Routes.ActivateCard.route)}>
                <Text style={styles.activateButtonText}>Activate Card</Text>
              </Button>
            </View>
          ) : null}
        </>
      ) : null}
      <Text style={styles.disclaimerText}>{disclaimer}</Text>
    </ScrollView>
  )
})

const styles = StyleSheet.create({
  activateCardContainer: {
    marginTop: 32,
    width: cardImageStyle.width,
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
    borderRadius: 15,
  },
  cardTitle: {
    ...typography['label/xs-bold'],
    color: mesaColorNames['Primary/Brand #1C1C1C'],
  },
  cardBody: {
    ...typography['paragraph/sm'],
    color: mesaColorNames['Primary/Brand #1C1C1C'],
  },
  activateButtonText: {
    ...typography['label/md-bold'],
    color: mesaColorNames['Mesa - Brand/Secondary #223843'],
  },
  buttonsContainer: {
    marginVertical: 20,
    margin: 'auto',
    display: 'flex',
    flexDirection: 'row',
    gap: 28,
  },
  buttonOuterContainer: { display: 'flex', gap: 4, alignItems: 'center' },
  button: {
    minHeight: 56,
    minWidth: 56,
    width: 56,
    height: 56,
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: mesaColorNames['Mesa Primary Scale/500 #00C7B6'],
  },
  buttonText: {
    ...typography['label/xs-bold'],
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    width: 88,
    textAlign: 'center',
  },
  disclaimerText: {
    ...typography['paragraph/sm'],
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    marginHorizontal: 32,
    paddingVertical: 16,
    marginTop: 'auto',
  },
  shimmerPlaceholder: {
    height: 220,
    width: 350,
    borderRadius: 16,
  },
})
